// Review window for network shares — the share-side twin of publish-renewal.ts.
// Keep in sync with mobile/utils/share-renewal.ts and with the server filter in
// CRMContext (`shareStatus: 'expiring'`).
//
// Why this exists: shares auto-expire (180 days for sale, 90 for rent — see
// system_settings) and nothing ever warned anyone. Measured on prod 2026-08-14:
// 5,053 expired shares against 5,821 live ones — 46% of everything ever shared
// had silently dropped out of the exchange, 531 of them on listings still
// published, with 927 more due to lapse inside a month across 234 agencies.
//
// The window is 30 days, not the 7 used for publications: a 180-day share term
// warned 7 days out gives an agent no room to reach the owner and re-verify.
//
// Deliberately no bulk "renovar todas" — re-listing to the network is a
// commercial decision per property (price still right? still available? owner
// still on board?), so every surface built on this routes to one property.

export const SHARE_RENEWAL_WINDOW_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function isInShareRenewalWindow(shareExpiresAt: string | null | undefined): boolean {
  if (!shareExpiresAt) return false;
  const expiry = new Date(shareExpiresAt).getTime();
  if (Number.isNaN(expiry)) return false;
  return expiry - Date.now() <= SHARE_RENEWAL_WINDOW_DAYS * MS_PER_DAY;
}

// True when a share needs a look before it lapses: already expired, or still
// live but inside the window. Mirrors the server query exactly —
// share_status IN ('shared','expired') AND share_expires_at <= cutoff — so the
// dashboard count and the filtered grid can never disagree.
export function needsShareRenewal(property: {
  share_status?: string | null;
  share_expires_at?: string | null;
}): boolean {
  if (property.share_status !== 'shared' && property.share_status !== 'expired') return false;
  return isInShareRenewalWindow(property.share_expires_at);
}

// True when the share chip should read "Renovar" instead of "Compartida" /
// "Compartir", and the toggle should renew instead of unshare. Mirrors
// shouldShowRenovarPublish (publish-renewal.ts) exactly — same shape, 30-day
// window instead of 7.
//
// Why it exists: until 2026-09-07 the chip only said "Renovar" once the share
// had ALREADY lapsed. Inside the window it still read "Compartida — Exp: 5
// días", and clicking it silently unshared the property. The dashboard was
// nudging agents to act before expiry while the only button available did the
// opposite of what they wanted, and the support bot was telling them to press
// it. As with publications, unsharing is unavailable inside the window: renew
// first, unshare afterwards if that is really the intent.
export function shouldShowRenovarShare(property: {
  share_status?: string | null;
  share_expires_at?: string | null;
}): boolean {
  if (property.share_status === 'expired') return true;
  if (property.share_status === 'shared') return isInShareRenewalWindow(property.share_expires_at);
  return false;
}

/** Upper bound for `share_expires_at` in server-side queries. */
export function shareRenewalCutoffISO(now: Date = new Date()): string {
  return new Date(now.getTime() + SHARE_RENEWAL_WINDOW_DAYS * MS_PER_DAY).toISOString();
}
